<template>
    <div v-if="config" class="flex flex-col">
        <div class="w-full flex">
            <div :class="[hideLateralDescriptionValue ? 'w-full' : 'w-1/2 pr-2', 'flex items-center gap-2', inputClass]">
                <Label :for="`control-${controlId}`" :class="[overrideLabelOpacity ? 'override-opacity' : '']">
                    {{ config.display_name }}
                </Label>
                <Checkbox
                    v-model="controlValue"
                    :id="`control-${controlId}`"
                    :disabled="!editMode"
                    :aria-invalid="errors.length > 0"
                    class="flex-shrink-0" />
                <Tooltip v-if="config.description && hideLateralDescriptionValue">
                    <TooltipTrigger asChild>
                        <span>
                            <Info class="h-4 w-4 text-muted-foreground" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent side="right" class="whitespace-normal max-w-xs">
                        <p class="text-xs" v-html="sanitizeHtml(config.description)" />
                    </TooltipContent>
                </Tooltip>
                <div v-if="errors.length" class="text-destructive mt-1 text-xs">
                    <template v-for="err in errors" :key="err.$uid">
                        <span>* {{ err.$message }}</span>
                    </template>
                </div>
            </div>
            <div v-if="!hideLateralDescriptionValue" class="w-1/2 pl-2">
                <Alert
                    v-if="config.description"
                    variant="default"
                    class="min-h-[calc(theme(spacing.10)+theme(spacing.6))] flex items-center p-3 text-xs border bg-muted/10">
                    <AlertDescription class="text-muted-foreground text-2xs">
                        <span v-html="sanitizeHtml(config.description)" />
                    </AlertDescription>
                </Alert>
            </div>
        </div>
    </div>
</template>

<script setup>
import { uuid4 } from '@sentry/utils';
import { sanitizeHtml } from '@/utils/utils';
import { ref, watch, onMounted, onUpdated, onUnmounted } from 'vue';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-vue-next';

const controlId = uuid4();

const emit = defineEmits(['input']);
const props = defineProps({
    config: {
        type: Object,
        required: true
    },
    value: {
        type: Boolean,
        default: undefined
    },
    editMode: {
        type: Boolean,
        default: true
    },
    overrideLabelOpacity: {
        type: Boolean,
        default: false
    },
    hideLateralDescription: {
        type: Boolean,
        default: false
    },
    errors: {
        type: Array,
        default: () => []
    },
    inputClass: {
        type: String,
        default: ''
    }
});

const hideLateralDescriptionValue = ref(props.hideLateralDescription);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});
const defaultValue = ref(props.config.value.default === null || props.config.value.default === undefined ? false : props.config.value.default);

const controlValue = ref(props.value === null || props.value === undefined ? defaultValue.value : props.value);

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

onMounted(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

watch(controlValue, (newValue) => {
    emit('input', newValue);
});

watch(
    () => props.value,
    (newVal) => {
        controlValue.value = newVal;
    }
);
</script>
